import type { CanvasPoint, Idea, IdeaPatch } from '../api'
import { STAGE_LABEL, stageOf, type Stage } from '../domain/stages'
import { ideasStore, updateIdea } from './ideas'
import { queueStore, setQueue } from './queue'
import { toast } from './toast'

/**
 * Move an idea to another stage, the one way every control does it: a drag
 * on the canvas, the step row in the editor.
 *
 * Ideas and Scripted are statuses; Filming is the next batch, which an idea
 * joins without changing status. So a move may touch the idea, the batch, or
 * both, and its Undo puts back both. To post is reached only by filming: the
 * pipeline moves a filmed idea on, and a filmed idea cannot be queued again.
 *
 * `at` is where on the canvas it was dropped, inside the target frame; `index`
 * where in the batch.
 */
export function moveToStage(idea: Idea, to: Stage, { at, index }: { at?: CanvasPoint; index?: number } = {}): void {
  const queue = queueStore.get().ids
  const from = stageOf(idea, queue)
  if (from === null || from === to) return
  if (to === 'post') {
    toast('Videos arrive in To post from the edit')
    return
  }
  if (from === 'post') {
    toast('This one is filmed already')
    return
  }

  const place = queue.indexOf(idea.id)
  const without = queue.filter((id) => id !== idea.id)
  // Undo touches only this idea's place in the batch, so a reorder made
  // while the toast is up survives it.
  const putBackInQueue = () => {
    const now = queueStore.get().ids.filter((id) => id !== idea.id)
    if (place >= 0) now.splice(Math.min(place, now.length), 0, idea.id)
    if (now.join() !== queueStore.get().ids.join()) void setQueue(now)
  }

  if (to === 'filming') {
    const position = Math.min(index ?? without.length, without.length)
    void setQueue([...without.slice(0, position), idea.id, ...without.slice(position)])
    toast(`${quoted(idea.title)} is number ${position + 1} in the next batch`, {
      action: { label: 'Undo', run: putBackInQueue },
    })
    return
  }

  if (place >= 0) void setQueue(without)
  const status = to === 'ideas' ? 'idea' : 'scripted'
  const patch: IdeaPatch = {}
  if (status !== idea.status) patch.status = status
  if (at) patch.canvas = at
  if (Object.keys(patch).length) void updateIdea(idea.id, patch)
  const undoIdea: IdeaPatch = { status: idea.status, canvas: idea.canvas }
  toast(`${quoted(idea.title)} moved to ${STAGE_LABEL[to]}`, {
    action: {
      label: 'Undo',
      run: () => {
        putBackInQueue()
        if (Object.keys(patch).length && ideasStore.get().ideas.some((item) => item.id === idea.id)) {
          void updateIdea(idea.id, undoIdea)
        }
      },
    },
  })
}

function quoted(title: string): string {
  const short = title.length > 32 ? `${title.slice(0, 31).trimEnd()}…` : title
  return `"${short}"`
}
